import { useState, useEffect, useCallback } from "react";
import { X, AlertCircle } from "lucide-react";
import type { Event, RecurrenceUnit } from "../types";
import { RECURRENCE_UNITS, RECURRENCE_UNIT_LABEL_KEYS } from "../constants";
import { ImageUploader } from "../../marketplace/components/ImageUploader";
import { useLocale } from "../../../context/LanguageContext";

// ─── Form State ──────────────────────────────────────────────────────────────

interface LocationState {
  address: string;
  city: string;
  country: string;
  isVirtual: boolean;
  meetingLink: string;
}

interface RecurrenceState {
  enabled: boolean;
  intervalValue: string;
  intervalUnit: RecurrenceUnit;
}

interface FormState {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  location: LocationState;
  recurrence: RecurrenceState;
}

function getInitialState(event?: Event): FormState {
  if (event) {
    return {
      name: event.name,
      description: event.description ?? "",
      startDate: event.startDate.slice(0, 10),
      endDate: event.endDate.slice(0, 10),
      startTime: event.startTime ?? "",
      endTime: event.endTime ?? "",
      location: {
        address: event.location?.address ?? "",
        city: event.location?.city ?? "",
        country: event.location?.country ?? "",
        isVirtual: event.location?.isVirtual ?? false,
        meetingLink: event.location?.meetingLink ?? "",
      },
      recurrence: {
        enabled: event.recurrence?.enabled ?? false,
        intervalValue: String(event.recurrence?.intervalValue ?? 1),
        intervalUnit: event.recurrence?.intervalUnit ?? "MONTHS",
      },
    };
  }
  return {
    name: "",
    description: "",
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    location: {
      address: "",
      city: "",
      country: "",
      isVirtual: false,
      meetingLink: "",
    },
    recurrence: { enabled: false, intervalValue: "1", intervalUnit: "MONTHS" },
  };
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface CreateEventDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (input: Record<string, unknown>, imageFile?: File | null) => void;
  loading: boolean;
  event?: Event;
  duplicateFrom?: Event;
  currentOrgId: string | null;
  successView?: React.ReactNode;
  error?: string | null;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CreateEventDrawer({
  isOpen,
  onClose,
  onSubmit,
  loading,
  event,
  duplicateFrom,
  currentOrgId,
  successView,
  error,
}: CreateEventDrawerProps) {
  const [form, setForm] = useState<FormState>(getInitialState(event));
  const [imageFile, setImageFile] = useState<File | null>(null);
  const { t } = useLocale();

  const isEdit = Boolean(event);

  useEffect(() => {
    if (isOpen) {
      if (duplicateFrom) {
        const state = getInitialState(duplicateFrom);
        state.name = `${duplicateFrom.name}${t("common.copySuffix")}`;
        state.startDate = "";
        state.endDate = "";
        setForm(state);
      } else {
        setForm(getInitialState(event));
      }
      setImageFile(null);
    }
  }, [isOpen, event, duplicateFrom, t]);

  const set = useCallback(
    (field: keyof FormState, value: string | boolean) =>
      setForm((prev) => ({ ...prev, [field]: value as string })),
    [],
  );

  const setLoc = useCallback(
    (field: keyof LocationState, value: string | boolean) =>
      setForm((prev) => ({
        ...prev,
        location: { ...prev.location, [field]: value },
      })),
    [],
  );

  // ─── Submit ────────────────────────────────────────────────────────────

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Incorporate selected times into date strings (default: 00:00 start, 23:59 end)
    // Use local Date constructor (no Z suffix) so the browser interprets the
    // user's input as local time, then .toISOString() converts to correct UTC.
    const startTime = form.startTime || '00:00';
    const endTime = form.endTime || '23:59';

    const input: Record<string, unknown> = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      startDate: new Date(`${form.startDate}T${startTime}`).toISOString(),
      endDate: new Date(`${form.endDate}T${endTime}`).toISOString(),
    };

    if (form.startTime) input.startTime = form.startTime;
    if (form.endTime) input.endTime = form.endTime;

    // Location
    const loc = form.location;
    if (loc.isVirtual || loc.address || loc.city || loc.country) {
      input.location = {
        isVirtual: loc.isVirtual,
        ...(loc.address && { address: loc.address.trim() }),
        ...(loc.city && { city: loc.city.trim() }),
        ...(loc.country && { country: loc.country.trim() }),
        ...(loc.isVirtual && loc.meetingLink && { meetingLink: loc.meetingLink.trim() }),
      };
    }

    // Auto-set org-scoped accessibility
    if (!isEdit) {
      input.accessibility = "ORG_ONLY";
      if (currentOrgId) {
        input.organizationId = currentOrgId;
      }
    }

    // Recurrence
    input.recurrence = {
      enabled: form.recurrence.enabled,
      intervalValue: parseInt(form.recurrence.intervalValue, 10) || 1,
      intervalUnit: form.recurrence.intervalUnit,
    };

    onSubmit(input, imageFile);
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col bg-card shadow-xl">
        {successView ? (
          successView
        ) : (
          <>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-foreground">
            {isEdit
              ? t("events.drawer.editTitle")
              : duplicateFrom
                ? t("events.drawer.duplicateTitle")
                : t("events.drawer.createTitle")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 hover:bg-muted"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form
          id="event-drawer-form"
          onSubmit={handleSubmit}
          className="flex-1 space-y-4 overflow-y-auto px-6 py-4"
        >
          {/* Name */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              {t("events.drawer.eventName")} *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              maxLength={100}
              required
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder={t("adminUI.events.createNamePlaceholder")}
            />
          </div>

          {/* Description */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              {t("events.drawer.description")}
            </label>
            <textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              maxLength={200}
              rows={3}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder={t("adminUI.events.createDescriptionPlaceholder")}
            />
          </div>

          {/* Cover Image */}
          <div>
            <ImageUploader
              label={t("adminUI.imageUploader.coverImage")}
              currentImageUrl={event?.photoUrl}
              onFileSelect={(file: File) => setImageFile(file)}
              onRemove={() => setImageFile(null)}
            />
          </div>

          {/* Date row */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("events.drawer.startDate")} *
              </label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => set("startDate", e.target.value)}
                required
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("events.drawer.endDate")} *
              </label>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => set("endDate", e.target.value)}
                required
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          {/* Time row */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("events.drawer.startTime")} *
              </label>
              <input
                type="time"
                value={form.startTime}
                onChange={(e) => set("startTime", e.target.value)}
                required
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("events.drawer.endTime")} *
              </label>
              <input
                type="time"
                value={form.endTime}
                onChange={(e) => set("endTime", e.target.value)}
                required
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          {/* Virtual toggle */}
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                checked={form.location.isVirtual}
                onChange={(e) => setLoc("isVirtual", e.target.checked)}
                className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
              />
              <div>
                <span className="text-sm font-medium text-foreground">
                  {t("events.drawer.virtualEvent")}
                </span>
                <p className="text-xs text-muted-foreground">
                  {t("events.drawer.virtualEventHint")}
                </p>
              </div>
            </label>
          </div>

          {/* Meeting link (virtual only) */}
          {form.location.isVirtual && (
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("events.drawer.meetingLink")}
              </label>
              <input
                type="url"
                value={form.location.meetingLink}
                onChange={(e) => setLoc("meetingLink", e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder={t("events.drawer.meetingLinkPlaceholder")}
              />
            </div>
          )}

          {/* Physical location (non-virtual) */}
          {!form.location.isVirtual && (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t("events.drawer.address")}
                </label>
                <input
                  type="text"
                  value={form.location.address}
                  onChange={(e) => setLoc("address", e.target.value)}
                  maxLength={200}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder={t("events.drawer.addressPlaceholder")}
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    {t("events.drawer.city")}
                  </label>
                  <input
                    type="text"
                    value={form.location.city}
                    onChange={(e) => setLoc("city", e.target.value)}
                    maxLength={100}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder={t("events.drawer.cityPlaceholder")}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    {t("events.drawer.country")}
                  </label>
                  <input
                    type="text"
                    value={form.location.country}
                    onChange={(e) => setLoc("country", e.target.value)}
                    maxLength={100}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder={t("events.drawer.countryPlaceholder")}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Autopilot: Recurrence */}
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                checked={form.recurrence.enabled}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    recurrence: { ...prev.recurrence, enabled: e.target.checked },
                  }))
                }
                className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
              />
              <div>
                <span className="text-sm font-medium text-foreground">
                  {t("events.drawer.recurring")}
                </span>
                <p className="text-xs text-muted-foreground">
                  {t("events.drawer.recurringHint")}
                </p>
              </div>
            </label>

            <div className={`mt-3 flex items-center gap-2 transition-opacity ${!form.recurrence.enabled ? 'opacity-40 pointer-events-none' : ''}`}>
              <span className="text-sm text-muted-foreground">{t("events.drawer.every")}</span>
              <input
                type="number"
                value={form.recurrence.intervalValue}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    recurrence: { ...prev.recurrence, intervalValue: e.target.value },
                  }))
                }
                min={1}
                max={365}
                disabled={!form.recurrence.enabled}
                aria-disabled={!form.recurrence.enabled}
                tabIndex={form.recurrence.enabled ? 0 : -1}
                className="w-20 rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed"
              />
              <select
                value={form.recurrence.intervalUnit}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    recurrence: {
                      ...prev.recurrence,
                      intervalUnit: e.target.value as RecurrenceUnit,
                    },
                  }))
                }
                disabled={!form.recurrence.enabled}
                aria-disabled={!form.recurrence.enabled}
                tabIndex={form.recurrence.enabled ? 0 : -1}
                className="rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed"
              >
                {RECURRENCE_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {t(RECURRENCE_UNIT_LABEL_KEYS[u])}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </form>

        {/* Error Banner */}
        {error && (
          <div className="mx-6 mb-2 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2.5">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="inline-flex h-9 items-center rounded-md border border-border px-4 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            form="event-drawer-form"
            disabled={loading}
            className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {loading
              ? t("events.drawer.saving")
              : isEdit
                ? t("events.drawer.update")
                : t("events.drawer.createDraft")}
          </button>
        </div>
          </>
        )}
      </div>
    </>
  );
}
